import { extractRTNumberFromSubdomain } from './excelToRoundTableTransform';

export interface ValidationIssue {
    row: number;
    clubName: string;
    column: string;
    value: any;
    message: string;
    rowData?: Record<string, any>; // Full row data for accessing treasurer email
}

export interface ValidationCheckResult {
    checkName: string;
    checkDescription: string;
    isValid: boolean;
    issues: ValidationIssue[];
}

export interface ContentValidationResult {
    isValid: boolean;
    checks: ValidationCheckResult[];
}

export type ValidationCheck = (data: Record<string, any>[]) => ValidationCheckResult;

/**
 * Validates IBAN format
 * Basic validation - checks length and alphanumeric characters
 */
const validateIBANFormat = (iban: string): boolean => {
    if (!iban || typeof iban !== 'string') return false;
    
    // Remove spaces and convert to uppercase
    const cleanIban = iban.replace(/\s/g, '').toUpperCase();
    
    // IBAN should be between 15 and 34 characters
    if (cleanIban.length < 15 || cleanIban.length > 34) return false;
    
    // Should start with 2 letters (country code) followed by 2 digits (check digits)
    const ibanRegex = /^[A-Z]{2}\d{2}[A-Z0-9]+$/;
    return ibanRegex.test(cleanIban);
};

/**
 * Validates BIC format
 * BIC/SWIFT code format: 8 or 11 alphanumeric characters
 */
const validateBICFormat = (bic: string): boolean => {
    if (!bic || typeof bic !== 'string') return false;
    
    // Remove spaces and convert to uppercase
    const cleanBic = bic.replace(/\s/g, '').toUpperCase();
    
    // BIC should be 8 or 11 characters
    if (cleanBic.length !== 8 && cleanBic.length !== 11) return false;
    
    // Format: 4 letters (institution code) + 2 letters (country code) + 2 alphanumeric (location) + optional 3 alphanumeric (branch)
    const bicRegex = /^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$/;
    return bicRegex.test(cleanBic);
};

/**
 * Check IBAN validity in all rows
 */
export const checkIBANValidity: ValidationCheck = (data: Record<string, any>[]) => {
    const issues: ValidationIssue[] = [];
    const ibanColumn = '[Bank Account] IBAN';
    const clubNameColumn = '[Club] Name';
    
    data.forEach((row, index) => {
        const iban = row[ibanColumn];
        if (iban && !validateIBANFormat(iban)) {
            issues.push({
                row: index + 1,
                clubName: row[clubNameColumn] || 'Unbekannt',
                column: ibanColumn,
                value: iban,
                message: `Ungültiges IBAN-Format: "${iban}"`,
                rowData: row
            });
        }
    });
    
    return {
        checkName: 'IBAN-Validierung',
        checkDescription: 'Überprüft, ob alle IBAN-Einträge einem gültigen Format entsprechen',
        isValid: issues.length === 0,
        issues
    };
};

/**
 * Check BIC validity in all rows
 */
export const checkBICValidity: ValidationCheck = (data: Record<string, any>[]) => {
    const issues: ValidationIssue[] = [];
    const bicColumn = '[Bank Account] BIC';
    const clubNameColumn = '[Club] Name';
    
    data.forEach((row, index) => {
        const bic = row[bicColumn];
        if (bic && !validateBICFormat(bic)) {
            issues.push({
                row: index + 1,
                clubName: row[clubNameColumn] || 'Unbekannt',
                column: bicColumn,
                value: bic,
                message: `Ungültiges BIC-Format: "${bic}"`,
                rowData: row
            });
        }
    });
    
    return {
        checkName: 'BIC-Validierung',
        checkDescription: 'Überprüft, ob alle BIC-Einträge einem gültigen Format entsprechen',
        isValid: issues.length === 0,
        issues
    };
};

/**
 * Check for duplicate table numbers in all rows
 */
export const checkDuplicateTableNumbers: ValidationCheck = (data: Record<string, any>[]) => {
    const issues: ValidationIssue[] = [];
    const subdomainColumn = '[Club] Subdomain';
    const clubNameColumn = '[Club] Name';
    
    // Map to track RT numbers and their occurrences
    const rtNumberMap = new Map<number, Array<{ row: number; clubName: string; subdomain: string; rowData: Record<string, any> }>>();
    
    // First pass: collect all RT numbers
    data.forEach((row, index) => {
        const subdomain = row[subdomainColumn];
        const rtNumber = extractRTNumberFromSubdomain(subdomain);
        const clubName = row[clubNameColumn] || 'Unbekannt';
        
        if (rtNumber !== null) {
            if (!rtNumberMap.has(rtNumber)) {
                rtNumberMap.set(rtNumber, []);
            }
            rtNumberMap.get(rtNumber)!.push({
                row: index + 1,
                clubName,
                subdomain,
                rowData: row
            });
        }
    });
    
    // Second pass: identify duplicates
    rtNumberMap.forEach((occurrences, rtNumber) => {
        if (occurrences.length > 1) {
            // Add an issue for each occurrence of the duplicate
            occurrences.forEach(occurrence => {
                const otherRows = occurrences
                    .filter(occ => occ.row !== occurrence.row)
                    .map(occ => occ.row)
                    .join(', ');
                
                issues.push({
                    row: occurrence.row,
                    clubName: occurrence.clubName,
                    column: subdomainColumn,
                    value: occurrence.subdomain,
                    message: `Doppelte Tischnummer ${rtNumber} (auch in Zeile(n): ${otherRows})`,
                    rowData: occurrence.rowData
                });
            });
        }
    });
    
    return {
        checkName: 'Doppelte Tischnummern',
        checkDescription: 'Überprüft, ob jede Tischnummer nur einmal vorkommt',
        isValid: issues.length === 0,
        issues
    };
};

/**
 * Registry of all validation checks
 * Add new checks here to extend validation
 */
export const VALIDATION_CHECKS: ValidationCheck[] = [
    checkIBANValidity,
    checkBICValidity,
    checkDuplicateTableNumbers,
    // Add more validation checks here as needed
];

/**
 * Runs all validation checks on the provided data
 * @param data - Array of data records to validate
 * @returns Validation result with all check results
 */
export const validateContent = (data: Record<string, any>[]): ContentValidationResult => {
    if (data.length === 0) {
        return { isValid: true, checks: [] };
    }
    
    const checks = VALIDATION_CHECKS.map(check => check(data));
    const isValid = checks.every(check => check.isValid);
    
    return {
        isValid,
        checks
    };
};
